import { Pipe, PipeTransform } from '@angular/core';

const BADGE_MAP: Record<string, string> = {
  // Alert types (todos widgets)
  danger: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  warning: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  info: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  success: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  // Priority levels (tasks)
  Urgent: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  High: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  Medium: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  Low: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  // Status values (tasks, eservices)
  Pending: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  'In Progress': 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  'On Hold': 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
  Cancelled: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  Closed: 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
  Completed: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  Approved: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  Rejected: 'bg-red-50 text-red-700 ring-1 ring-red-200',
};

const DEFAULT_BADGE = 'bg-gray-50 text-gray-600 ring-1 ring-gray-200';

@Pipe({ name: 'badge', standalone: true, pure: true })
export class BadgePipe implements PipeTransform {
  transform(value: string): string {
    return BADGE_MAP[value] ?? DEFAULT_BADGE;
  }
}

@Pipe({ name: 'alertLabel', standalone: true, pure: true })
export class AlertLabelPipe implements PipeTransform {
  private readonly labels: Record<string, string> = {
    danger: 'Critical',
    warning: 'Review',
    info: 'Info',
    success: 'Done',
  };

  transform(alert: string): string {
    return this.labels[alert] ?? alert;
  }
}

/**
 * Consistent severity color for "total count" badges across every dashboard:
 * < 5 → green, 5–10 → orange, > 10 → red.
 */
export function countSeverityColor(value: number | null | undefined): 'green' | 'orange' | 'red' {
  const n = value ?? 0;
  if (n < 5) return 'green';
  if (n <= 10) return 'orange';
  return 'red';
}

/** Solid-style classes (bg-X-100/text-X-700) — for building `countBadgeClass` in .ts code, e.g. IconCountItem. */
export function countBadgeClasses(value: number | null | undefined): string {
  return `bg-${countSeverityColor(value)}-100 text-${countSeverityColor(value)}-700`;
}

/** Ring-style classes (bg-X-50/text-X-600/ring-X-200) — for building `countBadgeClass` in .ts code. */
export function countBadgeRingClasses(value: number | null | undefined): string {
  const color = countSeverityColor(value);
  return `bg-${color}-50 text-${color}-600 ring-1 ring-${color}-200`;
}

@Pipe({ name: 'countBadge', standalone: true, pure: true })
export class CountBadgePipe implements PipeTransform {
  transform(value: number | null | undefined): string {
    return countBadgeClasses(value);
  }
}

@Pipe({ name: 'countBadgeRing', standalone: true, pure: true })
export class CountBadgeRingPipe implements PipeTransform {
  transform(value: number | null | undefined): string {
    return countBadgeRingClasses(value);
  }
}
